import { Key, useEffect, useMemo, useState } from "react";
import {
  DndContext,
  DragEndEvent,
  DragOverEvent,
  DragOverlay,
  DragStartEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { SortableContext, arrayMove } from "@dnd-kit/sortable";
import { createPortal } from "react-dom";
import ColumnContainer from "./users-board-column";
import { DictType, Task } from "@pma/types";
import UsersTaskCard from "./users-task-card";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useModal } from "@stores/modal-store";
import { PlusIcon } from "lucide-react";
import useTranslator from "@hooks/use-translator";
import { editTaskByCurrentUser, getUserandTask } from "@pma/utils/utils";
import { toast } from "@pma/hooks/use-toast";
import axios from "axios";
import { SERVER_URL } from "@pma/utils/api-routes";
import { parseCookies } from "nookies";
import { ACCESS_TOKEN, queryClient } from "@pma/utils/constants";
import { useAuth } from "@pma/stores/auth-store";

interface Props {
  tasks: Task[];
}

function UsersBoard({ tasks: projectTasks }: Props) {
  const [columns, setColumns] = useState<any[]>([{}]);

  const [tasks, setTasks] = useState<any[]>([{}]);

  const [activeColumn, setActiveColumn] = useState<any | null>(null);

  const [activeTask, setActiveTask] = useState<any | null>(null);

  const [searchParam] = useSearchParams();
  const newParams = new URLSearchParams(searchParam); 

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 10,
      },
    }),
  );

  const modal = useModal();

  const { translate } = useTranslator();

  const navigate = useNavigate();

  const userKey = 'user-order'
const { authConfig } = useAuth();
  
    const userId = authConfig?.id;

    const columnOrder = localStorage.getItem(userKey)
    useEffect(()=>{
      if(!columnOrder){
        localStorage.setItem(userKey,JSON.stringify([]))
      }
      if(columnOrder){
        const userArr = JSON.parse(columnOrder)
        const found = userArr.find((val:any)=>val.user===userId)
  
        if(!found){
          localStorage.setItem(userKey,JSON.stringify([...userArr,{user:userId,data:[]}]))
        }
      }
    },[])

  const updateTaskOwner = async (
    fromUserId: string,
    toUserId: number,
    task: DictType,
  ) => {
    let newResponsibleIdArray: Array<number> = [toUserId];
    task.responsible_id.forEach((user: DictType) => {
      if (fromUserId !== user.email) {
        if (newResponsibleIdArray.indexOf(user.email) === -1)
          newResponsibleIdArray.push(user.id);
      }
    });

    await editTaskByCurrentUser(task.id, {
      responsible_id: [...new Set(newResponsibleIdArray)],
      inform_users: true,
    })
      .then(() => {
        // Trigger Revalidate tasks
        newParams.set('revalidate', 'tasks');
        navigate(`?${newParams.toString()}`);
      })
      .catch((error: any) => {
        const errorResponse = error?.response?.data?.message;
        toast({
          title: translate("Error"),
          description: translate(errorResponse || "Unable to perform this action"),
          variant: "error",
        });
      });
  };

  const findCol = (id:number,iId:number)=>{
    let temp = iId
    const arr = localStorage?.getItem(userKey)
    if(arr){
      
      const userArr = JSON.parse(arr)

      if(userArr && userArr.length > 0){
        const found = userArr.find((val:any)=>val.user===userId )

        if(found){
          const userOrder = found.data
           const entry = userOrder.find((val:{id:number,order:number})=>id===val.id)
        if(entry){
          temp = entry.order
        }
        }
      }
    }
   return temp 
  }

  const usersColumns = useMemo(() => {
    const groupedTasks = projectTasks && getUserandTask(projectTasks);
    const allTasks: Array<any> = [];
    const cols = groupedTasks?.map((group,index) => {
      allTasks.push(...group?.tasks);
      return {
        id: group?.user?.id,
        title: group?.user?.name,
        picture: group?.user?.url,
        count: group?.tasks?.length,
        tasks: group?.tasks,
        order: findCol(group?.user?.id,index) 
      };
    });
    
    setTasks(allTasks);
    return cols.sort(
      (a: DictType, b: DictType) => a.order - b.order);;
  }, [projectTasks]);

  const columnsId = useMemo(
    () => usersColumns?.map((col: DictType) => col.id),
    [usersColumns],
  );

  function generateId() {
    /* Generate a random number between 0 and 10000 */
    return Math.floor(Math.random() * 10001);
  }

  function createTask(columnId: any) {
    const newTask: any = {
      id: generateId(),
      columnId,
      content: `Task ${tasks?.length + 1}`,
    };

    setTasks([...tasks, newTask]);
  }

  function deleteTask(id: any) {
    const newTasks = tasks?.filter((task) => task.id !== id);
    setTasks(newTasks);
  }

  function updateTask(id: any, content: string) {
    const newTasks = tasks?.map((task) => {
      if (task.id !== id) return task;
      return { ...task, content };
    });

    setTasks(newTasks);
  }


  function deleteColumn(id: any) {
    const filteredColumns = columns?.filter((col) => col.id !== id);
    setColumns(filteredColumns);

    const newTasks = tasks.filter((t) => t.columnId !== id);
    setTasks(newTasks);
  }

  function updateColumn(id: any, title: string) {
    const newColumns = columns.map((col) => {
      if (col.id !== id) return col;
      return { ...col, title };
    });

    setColumns(newColumns);
  }

  function onDragStart(event: DragStartEvent) {
    if (event.active.data.current?.type === "Column") {
      setActiveColumn(event.active.data.current.column);
      return;
    }
    
    if (event.active.data.current?.type === "Task") {
      setActiveTask(event.active.data.current.task);
      return;
    }
  }

  const assignUser = async (taskId: number, users: number[]) => {
   await axios.post(
      SERVER_URL + '/tasks/edit/byCurrentUser',
      {
        responsible_id: users,
        task_id: taskId,
        inform_users: true,
      },
      {
        headers: {
          Authorization: `Bearer ${parseCookies()[ACCESS_TOKEN]}`,
        },
      },
    ).then((res)=>{
      const {status} = res;
      if(status === 200){
        queryClient.invalidateQueries({ queryKey: ['get-tasks'] })
      }
    }).catch((err)=>{
      console.log(err,"err");
      
    })

  
  }

  function onDragEnd(event: DragEndEvent) {
    setActiveColumn(null);
    setActiveTask(null);
    const { active, over } = event;
    
    if (!over) return;
    const activeId = active.id;
    const overId = over.id;
    if (activeId === overId) return;

    const isActiveAColumn = active.data.current?.type === "Column";
    if (!isActiveAColumn) return;
    
    const isActiveCard = active.data.current?.type === "Task";
    const isOverColumn = over.data.current?.type === "Column";

    setColumns((columns) => {
     const activeColumnIndex = columns?.findIndex(
        (col) => col.id === activeId,
      );
      
      const overColumnIndex = columns?.findIndex((col) => col.id === overId);

      const arr = arrayMove(columns, activeColumnIndex, overColumnIndex);

      const tArr = arr.map((val,index)=>{
        val.order = index
        return {id:val.id,order:val.order}
      })

      if(columnOrder){
        const userArr = JSON.parse(columnOrder)
        if(userArr && userArr.length > 0){
          const found = userArr.find((val:any)=>val.user===userId)
          
          if(found){
            const index = userArr.indexOf(found)
            userArr[index]= {user:userId,data:tArr}        
                
            localStorage.setItem(userKey,JSON.stringify(userArr))
          }
        }else{
          localStorage.setItem(userKey,JSON.stringify([{data:tArr,user:userId}]))
        }
        
      }
      
      return arr
    });

    // if(isActiveCard && isOverColumn){ 
    //    const ac_id  = active.data.current ? active.data.current.task.id : ''
    //   const activeIndex = tasks.findIndex((t) => t.id === ac_id);
    //   const activeCard = tasks[activeIndex]
    //   const responsibleArr = [...activeCard?.responsible_id]
      
    //   const user = columns?.find((val)=>val.id===over.id)
    //   const userIndex = columns.indexOf(user)
      
    //   const responsibleIds = user.tasks.map((val:any)=>val.id)
      
    //   if(responsibleArr?.length > 0 && !responsibleIds.includes(activeCard.id)){
    //     const newCardEntry = {
    //       id: activeCard.id,
    //       uqId:activeCard.id+user.id,
    //       title: activeCard.title,
    //       dec: activeCard.description,
    //       status: activeCard.status,
    //       responsible_id: activeCard.responsible_id,
    //       user: user,
    //       notes_id: activeCard?.notes_id,
    //       _subtasks: activeCard?._subtasks,
    //       linked_files_id: activeCard?.linked_files_id,
    //     }
    //     const taskArr = [...user.tasks];
    //     taskArr.push(newCardEntry)
        
    //     const sortArr = arrayMove(taskArr, 0, 1);
        
    //     user.tasks = taskArr
    //     const newArr = [...columns]
    //     if(!responsibleArr.includes(user)){

    //       responsibleArr.push(user)
    //       newArr[userIndex] = user 
    //       setColumns(newArr)
    //       const ids = responsibleArr.map((val)=>val.id)
    //       assignUser(ac_id as number,ids)
    //     }
    //   }

    // }
   
  }

  function onDragOver(event: DragOverEvent) {
    const { active, over } = event;
    
    
    
    if (!over) return;

    const activeId = active.id;
    const overId = over.id;
    if (activeId === overId) return;
    const isActiveATask = active.data.current?.type === "Task";
    const isOverATask = over.data.current?.type === "Task";

    if (!isActiveATask) return;
    const isOverAColumn = over.data.current?.type === "Column";
    
    // dropping a Task over a column
    // if (isActiveATask && isOverAColumn) {
    //   const ac_id  = active.data.current ? active.data.current.task.id : ''
    //   const activeIndex = tasks.findIndex(
    //     (t: DictType) => t?.id === ac_id,
    //   );
     
    //   const activeTask: DictType = tasks[activeIndex];
    //   const user = over.data.current ? over.data.current.column : ''
      
    //   const responsibleArr = [...activeTask?.responsible_id]
      
    //   const isExistingUser = responsibleArr?.find((val)=>val.id===overId)
      
    //   if(responsibleArr && !isExistingUser ){  
    //       const assignToUser = {id:user.id,name:user.title,user_picture:user.picture}
    //       responsibleArr.push(assignToUser)
    //       activeTask['responsible_id']=responsibleArr
    //       const taskArr = [...tasks]
    
    //       for (let index = 0; index < taskArr.length; index++) {
    //         const element = taskArr[index];
            
    //         if(element.id===activeTask.id){
    //           element['responsible_id']=responsibleArr;
    //           break;
    //         }
    //       }
         
    //       const indexTarget = user.tasks.indexOf(activeTask)
    //       const newArr =  arrayMove([...user.tasks], indexTarget, user.tasks.length - 1)
         
         
    //       setTasks([...taskArr])
        
    //   }
    // }
    
    if (isActiveATask && isOverATask) {
      
      const activeIndex = tasks.findIndex((t) => t.uqId === activeId);
      const overIndex = tasks.findIndex((t) => t.uqId === overId);

      const activeCardResponsible = tasks[activeIndex].responsible_id
      const overCardResponsible = tasks[overIndex].responsible_id
      let found = {}
      for (let index = 0; index < activeCardResponsible.length; index++) {
        const element = activeCardResponsible[index];
        if(overCardResponsible.includes(element)){
          found = element
          break;
        }
      }
      if (Object.values(found).length === 0) {
        tasks[activeIndex].responsible_id = [...activeCardResponsible,tasks[overIndex].user]
        const arr = [...tasks]
        const newArr =  arrayMove([...arr], activeIndex, overIndex - 1)
        setTasks([...newArr])
      }
      else{
        const arr = [...tasks]

        const newArr = arrayMove([...arr], activeIndex, overIndex);       
        setTasks([...newArr])
      }
      
      if(over.data.current && over.data.current?.task){
        
        const responsibleArr = tasks[activeIndex].responsible_id
        const ids = responsibleArr.map((val:any)=>val.id)
              assignUser(tasks[activeIndex].id as number,ids)
        
      }
     
  }
  
  if (isActiveATask && isOverAColumn) {

    const activeIndex = tasks?.findIndex((t) => t?.uqId === activeId);
    
    const activeTask = tasks[activeIndex];
    const activeCardResponsible = activeTask.responsible_id
    const overObj = over.data.current && over.data.current.column
    const userObj = {id:overObj.id,name:overObj.title,user_picture:overObj.picture}
   
    activeTask.responsible_id = [...activeCardResponsible,userObj]
    
    const arr = [...tasks]
    const newArr = arrayMove([...arr], activeIndex, activeIndex);
    setTasks([...newArr])

    const responsibleArr = activeTask.responsible_id
        const ids = responsibleArr.map((val:any)=>val.id)
              assignUser(activeTask.id as number,ids)
  }


  }
  useEffect(() => {
    setColumns(usersColumns);
  }, [projectTasks]);

  if (projectTasks) {
    return (
     
      <div className="flex justify-start w-full p-1 items-center overflow-x-auto overflow-y-hidden py-2">
        <DndContext
          sensors={sensors}
          onDragStart={onDragStart}
          onDragEnd={onDragEnd}
          onDragOver={onDragOver}
        >
          <div className="min-w-fit flex gap-4">
            <div className="flex gap-4">
              <SortableContext items={columnsId}>
                {columns?.map((col) => (
                  <ColumnContainer
                    key={col.id}
                    column={col}
                    deleteColumn={deleteColumn}
                    updateColumn={updateColumn}
                    createTask={createTask}
                    deleteTask={deleteTask}
                    updateTask={updateTask}
                    // tasks={tasks.length > 0 && tasks[0]?.user ? [...tasks.filter((task)=>task.user.id===col.id)] : [...tasks]}
                    tasks={[...tasks.filter((task)=>task?.user?.id===col?.id)]}
                  />
                ))}
              </SortableContext>
            </div>
            <button
              onClick={() => {
                modal.onOpen();
                modal.setContext("task");
              }}
              className="flex items-center h-[60px] w-[350px] min-w-[350px] cursor-pointer gap-2 rounded-lg border-1 border-primary bg-primary/20 p-4 text-primary ring-slate-300 hover:ring-2"
            >
              <PlusIcon />

              {translate("Add Task")}
            </button>
          </div>

          {createPortal(
            <DragOverlay>
              {activeColumn && (
                <ColumnContainer
                  column={activeColumn}
                  deleteColumn={deleteColumn}
                  updateColumn={updateColumn}
                  createTask={createTask}
                  deleteTask={deleteTask}
                  updateTask={updateTask}
                  tasks={tasks?.filter(
                    (task: DictType) => task?.user?.id === activeColumn.id,
                  )}
                />
              )}

              {activeTask && (
                <UsersTaskCard
                  task={activeTask}
                  deleteTask={deleteTask}
                  updateTask={updateTask}
                />
              )}
            </DragOverlay>,
            document.body,
          )}
        </DndContext>
      </div>
    );
  }
}

export default UsersBoard;
